import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { facilitiesApi, facilityTypesApi, handleApiError } from '../lib/api-client';
import type { CreateFacilityDto, UpdateFacilityDto, CreateFacilityTypeDto, UpdateFacilityTypeDto, FacilityTypeDto, CreateFacilitySubcategoryDto, UpdateFacilitySubcategoryDto } from '../types/api';

export const useFacilities = (params?: {
  locationId?: string;
  facilityTypeId?: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['facilities', params],
    queryFn: () => facilitiesApi.getAll(params),
  });
};

export const useFacility = (id: string) => {
  return useQuery({
    queryKey: ['facilities', id],
    queryFn: () => facilitiesApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateFacility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFacilityDto) => facilitiesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['assessable-facilities'] });
      toast.success('Facility created successfully');
    },
    onError: handleApiError,
  });
};

export const useUpdateFacility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFacilityDto }) => facilitiesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Facility updated successfully');
    },
    onError: handleApiError,
  });
};

export const useDeactivateFacility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => facilitiesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Facility deactivated successfully');
    },
    onError: handleApiError,
  });
};

export const useFacilityTypes = () => {
  return useQuery({
    queryKey: ['facility-types'],
    queryFn: () => facilityTypesApi.getAll(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useCreateFacilityType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFacilityTypeDto) => facilityTypesApi.create(data),
    onSuccess: (created: FacilityTypeDto) => {
      // Update active views immediately, then revalidate from backend.
      queryClient.setQueryData<FacilityTypeDto[]>(['facility-types'], old => {
        const current = old ?? [];
        if (current.some(x => x.id === created.id)) return current;
        return [...current, created];
      });

      queryClient.setQueryData<any[]>(['facility-types-catalog'], old => {
        const current = old ?? [];
        if (current.some((x: any) => x?.id === created.id)) return current;
        return [...current, created as any];
      });

      queryClient.invalidateQueries({ queryKey: ['facility-types'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['facility-types-catalog'], exact: true });
      toast.success('Facility type created successfully');
    },
    onError: handleApiError,
  });
};

export const useUpdateFacilityType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFacilityTypeDto }) =>
      facilityTypesApi.update(id, data),
    onSuccess: (updated: FacilityTypeDto) => {
      queryClient.setQueryData<FacilityTypeDto[]>(['facility-types'], old =>
        (old ?? []).map(ft => (ft.id === updated.id ? { ...ft, ...updated } : ft))
      );
      queryClient.invalidateQueries({ queryKey: ['facility-types'], exact: true });
      toast.success('Facility type updated');
    },
    onError: handleApiError,
  });
};

export const useDeleteFacilityType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => facilityTypesApi.softDelete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<FacilityTypeDto[]>(['facility-types'], old => {
        const current = old ?? [];
        return current.filter(ft => ft.id !== id);
      });
      queryClient.invalidateQueries({ queryKey: ['facility-types'], exact: true });
      toast.success('Facility type deleted successfully');
    },
    onError: handleApiError,
  });
};

export const useFacilitiesDropdown = (params?: { locationId?: string; facilityTypeId?: string }) => {
  return useQuery({
    queryKey: ['facilities-dropdown', params],
    queryFn: () => facilitiesApi.getDropdown(params),
    enabled: true,
  });
};

export const useCreateSubcategory = (facilityTypeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFacilitySubcategoryDto) => facilityTypesApi.createSubcategory(facilityTypeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-types'] });
      toast.success('Subcategory created');
    },
    onError: handleApiError,
  });
};

export const useUpdateSubcategory = (facilityTypeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFacilitySubcategoryDto }) =>
      facilityTypesApi.updateSubcategory(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-types'] });
      toast.success('Subcategory updated');
    },
    onError: handleApiError,
  });
};

export const useDeleteSubcategory = (facilityTypeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => facilityTypesApi.deleteSubcategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-types'] });
      toast.success('Subcategory deleted');
    },
    onError: handleApiError,
  });
};
